import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { buildProductPath } from '../utils/productSlug';
import { collection, doc, getDoc, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import usePageView from '../hooks/usePageView';
import Loading from './Loading';
import ProductGridItem from './ProductGridItem';
import {
  MAX_CONTENT_WIDTH,
  SPACING_CARD_BODY,
  SPACING_CARD,
  SPACING_SECTION,
  SPACING_BUTTON,
  SPACING_COMPONENT,
  SPACING_GROUP,
} from '../constants/layout';
import { FONT_TITLE2, FONT_TITLE2_MOBILE } from '../constants/typography';

const parseDate = value =>
  !value ? null : value.toDate ? value.toDate() : new Date(value);

const isProductActive = product => {
  const deactivatedDate = parseDate(product?.deactivatedDate);
  if (!deactivatedDate) return true;
  const today = new Date();
  return today <= deactivatedDate;
};

export default function CategoryDetail() {
  const { id } = useParams();
  usePageView('category', id);
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [loadingCategory, setLoadingCategory] = useState(true);
  const [products, setProducts] = useState([]);
  const [imageItems, setImageItems] = useState([]);
  const [loadedThumbnails, setLoadedThumbnails] = useState(new Set());

  useEffect(() => {
    let isMounted = true;

    async function fetchCategory() {
      if (!id) return;
      try {
        const ref = doc(db, 'categories', id);
        const snap = await getDoc(ref);
        if (!isMounted) return;
        if (snap.exists()) {
          setCategory({ id: snap.id, ...snap.data() });
        } else {
          setCategory(null);
        }
      } finally {
        if (isMounted) {
          setLoadingCategory(false);
        }
      }
    }

    fetchCategory();

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const snap = await getDocs(collection(db, 'products'));
      setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    })();
  }, [id]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'imageItems'), snap => {
      const allImages = snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          caption: data.caption ?? '',
          imageURL: data.imageURL ?? '',
          productID: data.productID ?? null,
          dateAdded: parseDate(data.dateAdded),
        };
      });
      setImageItems(allImages);
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    setLoadedThumbnails(new Set());
  }, [id]);

  useEffect(() => {
    if (category?.title) {
      document.title = category.title;
      return () => {
        document.title = 'Catalog';
      };
    }
    return undefined;
  }, [category?.title]);

  const categoryProductIds = useMemo(
    () => (Array.isArray(category?.itemIDs) ? category.itemIDs : []),
    [category?.itemIDs]
  );

  const categoryProducts = useMemo(() => {
    return products
      .filter(prod => categoryProductIds.includes(prod.id))
      .filter(isProductActive)
      .sort((a, b) => a.productName.localeCompare(b.productName));
  }, [products, categoryProductIds]);

  const getCoverImage = product => {
    let coverImage = null;
    if (product.productImage?.imageItemID) {
      coverImage = imageItems.find(img => img.id === product.productImage.imageItemID);
    }
    if (!coverImage) {
      coverImage = imageItems.find(img => img.productID === product.id) || null;
    }
    return coverImage;
  };

  const handleThumbnailLoad = productId => {
    setLoadedThumbnails(prev => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  };

  if (loadingCategory) {
    return <Loading />;
  }

  if (!category) {
    return (
      <div className="category-detail-container">
        <div className="category-header">
          <h1 className="category-title">Category not found</h1>
          <p className="category-description">
            We couldn't find the category you're looking for.
          </p>
        </div>
        <style jsx>{`
          .category-detail-container {
            max-width: ${MAX_CONTENT_WIDTH};
            margin: 0 auto;
            padding: ${SPACING_CARD_BODY} ${SPACING_CARD} ${SPACING_SECTION};
            padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          }

          .category-header {
            margin-bottom: ${SPACING_BUTTON};
          }

          .category-title {
            font-size: ${FONT_TITLE2};
            font-family: 'Instrument Serif', serif;
            margin: 0;
          }

          .category-description {
            color: #4b5563;
            margin-top: ${SPACING_COMPONENT};
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="category-detail-container">
      <div className="category-header">
        <h1 className="category-title">{category.title}</h1>
        {category.description ? (
          <p className="category-description">{category.description}</p>
        ) : null}
      </div>

      <div className="products-wrapper">
        {categoryProducts.length === 0 ? (
          <p className="empty-state">No products are currently available in this category.</p>
        ) : (
          <div className="product-grid">
            {categoryProducts.map(product => (
              <ProductGridItem
                key={product.id}
                product={product}
                coverImage={getCoverImage(product)}
                onClick={() => navigate(buildProductPath(product))}
              />
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .category-detail-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_CARD_BODY} ${SPACING_CARD} ${SPACING_SECTION};
          padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
          padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
        }

        .category-header {
          margin-bottom: ${SPACING_BUTTON};
        }

        .category-title {
          font-size: ${FONT_TITLE2};
          font-family: 'Instrument Serif', serif;
          letter-spacing: -0.01em;
          margin: 0;
        }

        .category-description {
          color: #4b5563;
          margin-top: ${SPACING_COMPONENT};
          line-height: 1.6;
        }

        .products-wrapper {
          margin-bottom: ${SPACING_GROUP};
        }

        .empty-state {
          color: #6b7280;
        }

        .product-grid {
          display: grid;
          width: 100%;
          row-gap: 16px;
          column-gap: 16px;
          grid-template-columns: repeat(4, 1fr);
        }

        @media (min-width: 1400px) {
          .product-grid {
            grid-template-columns: repeat(5, 1fr);
          }
        }

        @media (min-width: 1000px) and (max-width: 1399px) {
          .product-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        @media (max-width: 999px) {
          .product-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 600px) {
          .product-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 600px) {
          .category-title {
            font-size: ${FONT_TITLE2_MOBILE};
          }
        }
      `}</style>
    </div>
  );
}
